import { ReactNode, useEffect } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTelegramApp } from '../hooks/useTelegramApp';
import { useWalletStore } from '../store/wallet';
import WalletButton from './WalletButton';
import NetworkSwitcher from "./NetworkSwitcher";
import TestnetBanner from "./TestnetBanner";
import OnboardingTyJolt from "./OnboardingTyJolt";
import ForecastCarousel from './ForecastCarousel';
import { PointsDisplay } from './points/PointsDisplay';
import NotificationCenter from './ui/NotificationCenter';
import { RainIcon, ChartIcon, UsersIcon, ProfileIcon, MapPinIcon, BarChartIcon, JournalIcon, ReferralIcon, BookIcon, SettingsIcon } from './icons/WeatherIcons';
import FeedbackButton from './FeedbackButton';
import './Layout.css';

const ADMIN_ADDRESSES: string[] = [
  'UQAdminPlaceholder1111111111111111111111111111111',
  'UQAdminPlaceholder2222222222222222222222222222222',
];

interface LayoutProps {
    children: ReactNode;
    onShowGuide?: () => void;
}

function Layout({ children, onShowGuide }: LayoutProps) {
    const { t } = useTranslation();
    const { webApp, ready } = useTelegramApp();
    const location = useLocation();
    const navigate = useNavigate();
    const { address } = useWalletStore();
    const isAdmin = !!(address && ADMIN_ADDRESSES.includes(address));

    // Handle Telegram Back Button
    useEffect(() => {
        if (!ready || !webApp) return;

        // Set Header Color to match Deep Space theme
        webApp.setHeaderColor('#05070A');
        webApp.setBackgroundColor('#05070A');

        const backButton = webApp.BackButton;

        if (location.pathname !== '/' && location.pathname !== '/forecast' && location.pathname !== '/markets') {
            backButton.show();
            backButton.onClick(() => navigate(-1));
        } else {
            backButton.hide();
        }

        return (
) => {
            backButton.offClick(() => navigate(-1));
        };
    }, [webApp, ready, location.pathname, navigate]);

    return (
        <div className="layout">
          <TestnetBanner />
          <OnboardingTyJolt />
          
            <header className="header glass-panel">
                <div className="header-mesh"></div>
                <div className="header-content">
                    <div className="header-left">
                        <Link to="/forecast" className="logo-link">
                            <img src="/whether_logo.png" alt="Whether" className="logo-image" />
                        </Link>
                        <ForecastCarousel />
                    </div>
                    <div className="header-right">
                        {address && <PointsDisplay address={address} compact={true} />}
                        <NotificationCenter />
                        <Link to="/settings" className="settings-icon-btn" title="Settings" aria-label="Settings">
                            <SettingsIcon size={18} />
                        </Link>
                        <NetworkSwitcher />
                        <WalletButton />
                    </div>
                </div>
            </header>

            <main className="main">
                {children}
                <footer className="legal-footer">
                    <Link to="/legal/terms" className="legal-footer-link">Terms of Service</Link>
                    <span className="legal-footer-sep">·</span>
                    <Link to="/legal/privacy" className="legal-footer-link">Privacy Policy</Link>
                    <span className="legal-footer-sep">·</span>
                    <Link to="/legal/risks" className="legal-footer-link">Risk Disclosure</Link>
                </footer>
            </main>

                        {!["/", "/docs", "/legal"].includes(location.pathname) && <nav className="nav">
                <NavLink to="/perps" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                    <ChartIcon size={20} className="nav-icon" />
                    <span className="nav-label">Markets</span>
                </NavLink>
                <NavLink to="/wallet" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                    <span className="nav-icon" style={{fontSize: 18}}>&#x1F512;</span>
                    <span className="nav-label">Wallet</span>
                </NavLink>
                <NavLink to="/earn" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                    <BarChartIcon size={20} className="nav-icon" />
                    <span className="nav-label">Earn</span>
                </NavLink>
                <NavLink to="/docs" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                    <BookIcon size={20} className="nav-icon" />
                    <span className="nav-label">Docs</span>
                </NavLink>
                <a href="https://x.com/whethermarket" target="_blank" rel="noopener" className="nav-item">
                    <span className="nav-icon" style={{fontSize: 18}}>𝕏</span>
                    <span className="nav-label">X</span>
                </a>
                <a href="https://t.me/whethermarket" target="_blank" rel="noopener" className="nav-item">
                    <span className="nav-icon" style={{fontSize: 18}}>✈</span>
                    <span className="nav-label">Telegram</span>
                </a>
                {isAdmin && (
                    <NavLink to="/admin" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                        <SettingsIcon size={20} className="nav-icon" />
                        <span className="nav-label">Admin</span>
                    </NavLink>
                )}
                {onShowGuide && (
                    <button className="nav-item nav-guide-btn" onClick={onShowGuide} title="Open tutorial guide">
                        <span className="nav-icon-emoji">❓</span>
                        <span className="nav-label">Guide</span>
                    </button>
                )}
            </nav>}
            <FeedbackButton />
        </div>
    );
}

export default Layout;
